import React, { FC, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Button, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import Main from 'components/layout/Main';
import { CombinedStates, AppDispatch } from 'core/types';
import { ADMIN_ACCESS_LEVELS } from 'core/utils/value';
import TextInput from 'components/Elements/TextInput';
import { EditIcon, BinIcon, AddIcon, SearchIcon } from 'components/Elements/CustomSVGIcon';
import {
    fetchContacts, createContact, updateContact, deleteContact,
} from 'core/contacts/contactsApi';
import { Contact, ContactPayload } from 'core/contacts/types';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import ContactFormModal from './components/ContactFormModal';
import DeleteContactModal from './components/DeleteContactModal';
import './usefulContacts.scss';

const UsefulContacts: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const { contacts, isFetching, isMutating } = useSelector((state: CombinedStates) => state.contacts);

    const isAdmin = ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel);

    const [searchText, setSearchText] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [deletingContact, setDeletingContact] = useState<Contact | null>(null);
    const contactsById = useRef(new Map<number, Contact>());

    useEffect(() => {
        dispatch(fetchContacts());
    }, [dispatch]);

    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            dispatch(fetchContacts(value ? { search: value } : undefined));
        }, 300),
        [dispatch],
    );

    useEffect(() => () => {
        debouncedSearch.cancel();
    }, [debouncedSearch]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setSearchText(value);
        debouncedSearch(value.trim());
    };

    const openAddModal = () => {
        setEditingContact(null);
        setIsModalOpen(true);
    };

    const openEditModal = (contact: Contact) => {
        setEditingContact(contact);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingContact(null);
    };

    const handleSubmit = useCallback(async (payload: ContactPayload) => {
        if (editingContact) {
            const result = await dispatch(updateContact({ id: editingContact.id, payload }));
            if (updateContact.fulfilled.match(result)) {
                closeModal();
                return true;
            }
            return false;
        }
        const result = await dispatch(createContact(payload));
        if (createContact.fulfilled.match(result)) {
            closeModal();
            return true;
        }
        return false;
    }, [dispatch, editingContact]);

    const openDeleteModal = (contact: Contact) => {
        setDeletingContact(contact);
    };

    const closeDeleteModal = () => {
        setDeletingContact(null);
    };

    const handleConfirmDelete = useCallback(async () => {
        if (!deletingContact) return;
        const result = await dispatch(deleteContact({ id: deletingContact.id }));
        if (deleteContact.fulfilled.match(result)) {
            closeDeleteModal();
        }
    }, [deletingContact, dispatch]);

    // Keep a quick lookup of contacts by id so the custom row component can
    // resolve the row's notes without needing the cell to know about it.
    contactsById.current = new Map((contacts || []).map((c) => [c.id, c]));

    const columns = useMemo<ColumnsType<Contact>>(() => {
        const baseColumns: ColumnsType<Contact> = [
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                render: (email: string) => (email ? <a href={`mailto:${email}`}>{email}</a> : '-'),
            },
            {
                title: 'Phone Number',
                dataIndex: 'phoneNumber',
                key: 'phoneNumber',
            },
        ];

        if (isAdmin) {
            // Trailing column with no header — icon buttons appear on row hover
            // (CSS handles visibility). Reuses the `.date-extra-button` pattern
            // from FormDetails for visual consistency.
            baseColumns.push({
                // Render a non-breaking space so the TH cell still appears
                // (with the same banded header background as the others)
                // even though there's no visible label.
                title: ' ',
                key: 'actions',
                width: 96,
                align: 'right',
                className: 'actions-cell',
                render: (_: unknown, contact: Contact) => (
                    <div className="date-extra-button">
                        <Button
                            type="link"
                            className="edit-button"
                            onClick={() => openEditModal(contact)}
                            aria-label="Edit contact">
                            <EditIcon />
                        </Button>
                        <Button
                            type="link"
                            className="bin-button"
                            onClick={() => openDeleteModal(contact)}
                            aria-label="Delete contact">
                            <BinIcon />
                        </Button>
                    </div>
                ),
            });
        }

        return baseColumns;
    }, [isAdmin]);

    const toolbar = buildMainToolbar(userData?.role?.accessLevel);

    // Custom row component — wraps each <tr> in a single antd Tooltip so
    // hovering anywhere on the row shows the notes once (rather than three
    // separate cell tooltips). The tooltip is styled like a clean white
    // rounded card via the `useful-contacts-notes-tooltip` overlay class.
    const TooltipRow = useCallback((rowProps: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key'?: React.Key }) => {
        const rowKey = rowProps['data-row-key'];
        const rowId = typeof rowKey === 'number' ? rowKey : Number(rowKey);
        const contact = Number.isFinite(rowId) ? contactsById.current.get(rowId) : undefined;
        const notes = contact?.notes;

        if (!notes) {
            // eslint-disable-next-line react/jsx-props-no-spreading, jsx-a11y/control-has-associated-label
            return <tr {...rowProps} />;
        }
        return (
            <Tooltip
                title={notes}
                placement="bottom"
                mouseEnterDelay={0.2}
                overlayClassName="useful-contacts-notes-tooltip">
                {/* eslint-disable-next-line react/jsx-props-no-spreading, jsx-a11y/control-has-associated-label */}
                <tr {...rowProps} />
            </Tooltip>
        );
    }, []);

    const tableComponents = useMemo(() => ({
        body: {
            row: TooltipRow,
        },
    }), [TooltipRow]);

    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];
    const backLink = (
        <Button type="link" onClick={() => console.log('Back')}>
            <LeftOutlined /> BACK
        </Button>
    );

    return (
        <Main
            className="useful-contacts"
            title={<div>Useful Contacts</div>}
            hideSubHeader
            subHeaderTitle="Useful Contacts"
            subHeaderHasBackLink
            subHeaderBackLink="/"
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent=""
            floatingFooter={false}
            backLink={backLink}
            breadcrumb={navigation}>
            <div className="main-container useful-contacts-page">
                <h2>Useful Contacts</h2>
                <div className="contacts-toolbar">
                    <div className="search-wrapper">
                        <TextInput
                            leftIcon={<SearchIcon />}
                            placeholder="Search by name, email or phone"
                            value={searchText}
                            onChange={handleSearchChange} />
                    </div>
                    {isAdmin ? (
                        <Button type="primary" className="create-button" onClick={openAddModal}>
                            <AddIcon />
                            <div style={{ padding: 10 }}>Add Contact</div>
                        </Button>
                    ) : null}
                </div>
                <div className="contacts-content">
                    <Table<Contact>
                        dataSource={Array.isArray(contacts) ? contacts : []}
                        columns={columns}
                        rowKey="id"
                        loading={isFetching}
                        pagination={false}
                        components={tableComponents} />
                </div>
            </div>
            <ContactFormModal
                open={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                initialContact={editingContact}
                submitting={isMutating} />
            <DeleteContactModal
                open={Boolean(deletingContact)}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
                contact={deletingContact}
                submitting={isMutating} />
        </Main>
    );
};

export default UsefulContacts;
